import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Package, ArrowLeft, Plus, Search, Download, MoreVertical, Edit, Trash2, FileDown, ArrowUpDown, ArrowUp, ArrowDown, Copy } from "lucide-react";

export default function ArticlesPage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, article: null });
  const [downloadDialog, setDownloadDialog] = useState({ open: false, type: null, article: null });
  const [selectedSizes, setSelectedSizes] = useState({
    original: true,
    medium: true,
    thumbnail: true,
  });
  const [includeCSV, setIncludeCSV] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    let filtered = articles.filter(
      (article) =>
        article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (article.article_number && article.article_number.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';
        
        // Special handling for images count
        if (sortConfig.key === 'images') {
          aValue = a.images?.length || 0;
          bValue = b.images?.length || 0;
        }
        
        // Compare
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredArticles(filtered);
  }, [searchTerm, articles, sortConfig]);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data);
      setFilteredArticles(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Artikel");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyArticle = async (articleId) => {
    try {
      const response = await axios.post(`${API}/articles/${articleId}/copy`);
      toast.success(`Artikel kopiert: ${response.data.name}`);
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Kopieren des Artikels");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/articles/${deleteDialog.article.id}`);
      toast.success("Artikel erfolgreich gelöscht");
      fetchArticles();
    } catch (error) {
      toast.error("Fehler beim Löschen des Artikels");
    } finally {
      setDeleteDialog({ open: false, article: null });
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(`${API}/articles/export/csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "articles_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV Export erfolgreich");
    } catch (error) {
      toast.error("Fehler beim CSV Export");
    }
  };

  const openDownloadDialog = (type, article = null) => {
    // For batch downloads, create a job instead of opening dialog
    if (type === "batch") {
      handleCreateBatchDownloadJob();
      return;
    }
    setDownloadDialog({ open: true, type, article });
    setSelectedSizes({ original: true, medium: true, thumbnail: true });
    setIncludeCSV(false);
  };

  const handleCreateBatchDownloadJob = async () => {
    try {
      await axios.post(`${API}/downloads/batch`);
      toast.success("Batch-Download gestartet! Sie finden ihn auf der Downloads-Seite.");
      // Navigate to downloads page after a short delay
      setTimeout(() => navigate("/downloads"), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Downloads");
    }
  };

  const handleDownloadWithOptions = async () => {
    const sizesArray = Object.keys(selectedSizes).filter(key => selectedSizes[key]);
    
    if (sizesArray.length === 0) {
      toast.error("Bitte wählen Sie mindestens eine Auflösung");
      return;
    }

    try {
      const { type, article } = downloadDialog;
      let url = "";
      let filename = "";

      if (type === "single" && article) {
        const params = new URLSearchParams();
        sizesArray.forEach(size => params.append('sizes', size));
        url = `${API}/articles/${article.id}/images-zip?${params.toString()}`;
        filename = `${article.article_number}_images.zip`;
      } else if (type === "batch") {
        const params = new URLSearchParams();
        sizesArray.forEach(size => params.append('sizes', size));
        if (includeCSV) params.append('include_csv', 'true');
        url = `${API}/articles/batch-download?${params.toString()}`;
        filename = "all_articles_batch.zip";
      }

      const response = await axios.get(url, {
        responseType: "blob",
      });
      
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Download erfolgreich");
      setDownloadDialog({ open: false, type: null, article: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Download");
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1 text-blue-600" />
      : <ArrowDown className="w-4 h-4 ml-1 text-blue-600" />;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                size="icon"
                data-testid="back-button"
                className="border-slate-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Artikel</h1>
                  <p className="text-sm text-slate-600">{filteredArticles.length} Artikel gefunden</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {user?.role === "admin" && (
                <Button
                  onClick={() => navigate("/articles/new")}
                  data-testid="new-article-button"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-4 rounded-lg font-medium"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Neuer Artikel
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Export */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Artikel suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
              className="pl-10 h-11 border-slate-300"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadCSV}
              variant="outline"
              data-testid="csv-export-button"
              className="border-slate-300 hover:bg-slate-100 h-11 px-4"
            >
              <FileDown className="w-4 h-4 mr-2" />
              CSV Export
            </Button>
            <Button
              onClick={() => openDownloadDialog("batch")}
              variant="outline"
              data-testid="batch-download-button"
              className="border-slate-300 hover:bg-slate-100 h-11 px-4"
            >
              <Download className="w-4 h-4 mr-2" />
              Batch Download
            </Button>
          </div>
        </div>

        {/* Articles Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Package className="w-16 h-16 mb-4 text-slate-300" />
              <p className="text-lg font-medium">Keine Artikel gefunden</p>
              <p className="text-sm">Erstellen Sie Ihren ersten Artikel</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('sequential_id')}
                  >
                    <div className="flex items-center">
                      ID
                      {getSortIcon('sequential_id')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Kurzbeschreibung
                      {getSortIcon('name')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('article_number')}
                  >
                    <div className="flex items-center">
                      Artikelnummer
                      {getSortIcon('article_number')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('price')}
                  >
                    <div className="flex items-center">
                      Preis (netto)
                      {getSortIcon('price')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('weight')}
                  >
                    <div className="flex items-center">
                      Gewicht
                      {getSortIcon('weight')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('color')}
                  >
                    <div className="flex items-center">
                      Farbe
                      {getSortIcon('color')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('images')}
                  >
                    <div className="flex items-center">
                      Bilder
                      {getSortIcon('images')}
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredArticles.map((article) => (
                  <TableRow 
                    key={article.id} 
                    className="hover:bg-slate-50 cursor-pointer" 
                    data-testid={`article-row-${article.id}`}
                    onClick={() => user?.role === "admin" && navigate(`/articles/edit/${article.id}`)}
                  >
                    <TableCell className="text-slate-600 font-mono text-sm">{article.sequential_id || '-'}</TableCell>
                    <TableCell className="font-medium text-slate-900">{article.name}</TableCell>
                    <TableCell className="text-slate-600">
                      {article.article_number || <span className="text-slate-400 italic">-</span>}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {article.price != null ? `€${article.price.toFixed(2)}` : <span className="text-slate-400 italic">-</span>}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {article.weight != null ? `${article.weight}kg` : <span className="text-slate-400 italic">-</span>}
                    </TableCell>
                    <TableCell>
                      {article.color ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-700">
                          {article.color}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">{article.images?.length || 0}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`article-actions-${article.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {article.images?.length > 0 && (
                            <DropdownMenuItem
                              onClick={() => openDownloadDialog("single", article)}
                              data-testid={`download-images-${article.id}`}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Bilder herunterladen
                            </DropdownMenuItem>
                          )}
                          {user?.role === "admin" && (
                            <>
                              <DropdownMenuItem
                                onClick={() => navigate(`/articles/edit/${article.id}`)}
                                data-testid={`edit-article-${article.id}`}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Bearbeiten
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyArticle(article.id);
                                }}
                                data-testid={`copy-article-${article.id}`}
                              >
                                <Copy className="w-4 h-4 mr-2" />
                                Kopieren
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDeleteDialog({ open: true, article })}
                                className="text-red-600 focus:text-red-600"
                                data-testid={`delete-article-${article.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Löschen
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Download Options Dialog */}
      <Dialog open={downloadDialog.open} onOpenChange={(open) => !open && setDownloadDialog({ open: false, type: null, article: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download-Optionen</DialogTitle>
            <DialogDescription>
              Wählen Sie die Bildauflösungen für den Download
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700 font-medium mb-3 block">Bildauflösungen</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="size-original"
                    checked={selectedSizes.original}
                    onCheckedChange={(checked) => setSelectedSizes({ ...selectedSizes, original: checked })}
                  />
                  <label htmlFor="size-original" className="text-sm font-medium cursor-pointer">
                    Original (Volle Auflösung)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="size-medium"
                    checked={selectedSizes.medium}
                    onCheckedChange={(checked) => setSelectedSizes({ ...selectedSizes, medium: checked })}
                  />
                  <label htmlFor="size-medium" className="text-sm font-medium cursor-pointer">
                    Medium (800x800px)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="size-thumbnail"
                    checked={selectedSizes.thumbnail}
                    onCheckedChange={(checked) => setSelectedSizes({ ...selectedSizes, thumbnail: checked })}
                  />
                  <label htmlFor="size-thumbnail" className="text-sm font-medium cursor-pointer">
                    Thumbnail (200x200px)
                  </label>
                </div>
              </div>
            </div>
            
            {downloadDialog.type === "batch" && (
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-csv"
                    checked={includeCSV}
                    onCheckedChange={(checked) => setIncludeCSV(checked)}
                  />
                  <label htmlFor="include-csv" className="text-sm font-medium cursor-pointer">
                    CSV-Datei einschließen
                  </label>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDownloadDialog({ open: false, type: null, article: null })}
              className="border-slate-300"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleDownloadWithOptions}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, article: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Artikel löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Artikel "{deleteDialog.article?.name}" wirklich löschen? Diese Aktion kann nicht
              rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-button"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
